import type confetti from 'canvas-confetti'

import { prefersReducedMotion } from '@/utils/prefersReducedMotion'

export type ConfettiAnimationOptions = confetti.Options

/**
 * Same fields as
 * [canvas-confetti](https://github.com/catdad/canvas-confetti#confettioptions-object--promisenull)
 * plus optional `followUp` burst.
 */
export type ViewportConfettiOptions = Partial<ConfettiAnimationOptions> & {
  followUp?: Partial<ConfettiAnimationOptions> & { delayMs?: number }
  /** Third burst; delay is from the same origin as the first (parallel timers). */
  followUp2?: Partial<ConfettiAnimationOptions> & { delayMs?: number }
}

let canvas: HTMLCanvasElement | null = null
let instance: confetti.CreateTypes | null = null

/**
 * `canvas-confetti` (~10 KB gzip) is loaded lazily on the first burst so it never
 * lands in the shared bundle — the base `Button` references this module, which
 * would otherwise pull the library into nearly every route chunk. Cached after
 * the first dynamic import.
 */
async function ensureInstance(): Promise<confetti.CreateTypes | null> {
  if (typeof document === 'undefined') return null
  if (instance && canvas?.isConnected) return instance

  const { default: confetti } = await import('canvas-confetti')

  canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '2147483646',
  })
  document.body.appendChild(canvas)
  instance = confetti.create(canvas, { resize: true, useWorker: false })
  return instance
}

const DEFAULT_BURST: Partial<ConfettiAnimationOptions> = {
  particleCount: 100,
  spread: 65,
  startVelocity: 35,
  ticks: 240,
  gravity: 1.05,
  scalar: 1.05,
}

async function fireWithNormalizedOrigin(
  origin: { x: number; y: number },
  options?: ViewportConfettiOptions
): Promise<void> {
  // Decorative motion: skip entirely when the user prefers reduced motion
  // (WCAG 2.3.3). Covers every public entry point below.
  if (prefersReducedMotion()) return

  const shoot = await ensureInstance()
  if (!shoot) return

  const { followUp, followUp2, ...first } = options ?? {}
  void shoot({ ...DEFAULT_BURST, ...first, origin })

  if (followUp) {
    const { delayMs = 140, ...second } = followUp
    window.setTimeout(() => {
      void shoot({ ...DEFAULT_BURST, ...second, origin })
    }, delayMs)
  }

  if (followUp2) {
    const { delayMs = 300, ...third } = followUp2
    window.setTimeout(() => {
      void shoot({ ...DEFAULT_BURST, ...third, origin })
    }, delayMs)
  }
}

/** Bottom-right toast area (no booking card). */
export function fireViewportConfettiNearToastStack(
  options?: ViewportConfettiOptions
): void {
  void fireWithNormalizedOrigin({ x: 0.88, y: 0.9 }, options)
}

/**
 * Fire confetti from an element’s center (viewport coordinates).
 * Single canvas on `document.body`.
 */
export function fireViewportConfettiFromElement(
  element: HTMLElement,
  options?: ViewportConfettiOptions
): void {
  const r = element.getBoundingClientRect()
  const origin =
    r.width > 0 && r.height > 0
      ? {
          x: (r.left + r.width / 2) / window.innerWidth,
          y: (r.top + r.height / 2) / window.innerHeight,
        }
      : { x: 0.5, y: 0.5 }

  void fireWithNormalizedOrigin(origin, options)
}
